import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  CircularProgress,
  Stack,
  Box,
  Chip,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { EditAppointment } from '../../services/fhir';
import { usePostEvent } from '../../services/google/PostEvent';
import { useSessionContext } from '../../contexts/SessionContext';
import { useFhirData } from '../../utils/useFhirData';
import { useFormatter } from '../../utils/useFormatter';
import { FormButtonGroup } from '../form/FormButtonGroup';
import { SecondaryButton } from '../common/SecondaryButton';
import { useLocationContext } from '../../contexts/LocationContext';

interface GoogleCalendarSelectModalProps {
  open: boolean;
  onClose: () => void;
  scheduleId: string;
  appointments?: any; // Bundle de appointments já carregados
  nextAppointmentsPatients?: any[]; // Array de pacientes já carregados
  onSuccess?: () => void;
}

// Interface para o appointment processado
interface ProcessedAppointment {
  id: string;
  start: string;
  end: string;
  location?: string;
  locationDetail?: string;
  virtualServiceUrl?: string;
  practitioner?: string;
  healthcare?: string;
  patientName: string;
  appointment: any;
}

/**
 * Modal para selecionar appointments e adicionar ao Google Calendar
 * Permite seleção múltipla de appointments com status 'booked'
 */
export function GoogleCalendarSelectModal({
  open,
  onClose,
  appointments,
  nextAppointmentsPatients,
  onSuccess,
}: GoogleCalendarSelectModalProps) {
  const { user, accessTokenGoogle } = useSessionContext();
  const { postEvent } = usePostEvent();
  const { formatDateTimeToHour } = useFormatter;
  const { location } = useLocationContext();
  
  // Estados
  const [selectedAppointments, setSelectedAppointments] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingToCalendar, setAddingToCalendar] = useState(false);
  const [results, setResults] = useState<{ [id: string]: boolean }>({});
  const [processedAppointments, setProcessedAppointments] = useState<ProcessedAppointment[]>([]);

  // Função auxiliar para obter o endereço completo de uma localização
  const getLocationAddress = (locationId: string): string => {
    if (!location || location.length === 0) return '';
    
    const locationEntry = location.find(loc => loc.resource?.id === locationId);
    if (!locationEntry?.resource?.address) return '';
    
    const address = locationEntry.resource.address;
    const addressParts = [
      address.line?.join(', '),
      address.city,
      address.state,
      address.postalCode,
      address.country
    ].filter(Boolean);
    
    return addressParts.join(', ');
  };

  // Processa os appointments recebidos como prop
  useEffect(() => {
    async function processAppointments() {
      if (!open || !appointments) {
        return;
      }
      
      setLoading(true);
      try {
        // Filtra todos os agendamentos booked e futuros (sem filtrar por googleCalendarId ainda)
        const allBookedAppointments = appointments?.entry
          ?.filter((el: any) => {
            const isAppointment = el.resource?.resourceType === 'Appointment';
            const isBooked = el.resource?.status === 'booked';
            const isFuture = new Date(el?.resource?.end) > new Date(Date.now());
            
            return isAppointment && isBooked && isFuture;
          }) || [];
          
        // Filtra e mapeia os appointments que não têm googleCalendarId
        const bookedAppointments = allBookedAppointments
          ?.filter((el: any) => {
            // Verifica se já tem googleCalendarId
            const hasGoogleCalendarId = el.resource?.extension?.some(
              (ext: any) => ext.url === 'https://fluxmed.com.br/fhir/StructureDefinition/googleCalendarId'
            );
            
            return !hasGoogleCalendarId;
          })
          ?.map((appt: any): ProcessedAppointment => {
            const location = useFhirData.findParticipant(
              appt?.resource?.participant,
              'Location'
            )?.display;
            const practitioner = useFhirData.findParticipant(
              appt?.resource?.participant,
              'Practitioner'
            )?.display;
            const healthcare = useFhirData.findParticipant(
              appt?.resource?.participant,
              'HealthcareService'
            )?.display;

            // Busca informações adicionais sobre a localização
            let locationDetail = '';
            let virtualServiceUrl = '';
            const locationReference = appt?.resource?.participant?.find(
              (p: any) => p.actor?.reference?.includes('Location/')
            )?.actor?.reference;
            
            // Verifica se o appointment tem virtualService definido (primeira prioridade)
            if (appt?.resource?.virtualService && appt.resource.virtualService.length > 0) {
              const virtualService = appt.resource.virtualService[0];
              if (virtualService?.addressUrl) {
                virtualServiceUrl = virtualService.addressUrl;
              }
            }
            
            // Se não encontrou virtualService no appointment, busca informações da localização
            if (locationReference) {
              const locationId = locationReference.split('/')[1];
              
              // Primeiro tenta buscar no bundle de appointments
              const locationEntry = appointments?.entry?.find(
                (entry: any) =>
                  entry.resource?.resourceType === 'Location' &&
                  entry.resource?.id === locationId
              );
              
              // Verifica se é teleatendimento
              const isVirtual = locationEntry?.resource?.mode === 'virtual';
              
              if (isVirtual) {
                // Para teleatendimento, usa o nome do serviço virtual
                locationDetail = `Teleatendimento: ${locationEntry?.resource?.name || location || ''}`;
              } else if (locationEntry?.resource?.address) {
                // Para atendimento presencial, usa o endereço completo
                const address = locationEntry?.resource?.address;
                
                const addressParts = [
                  address.line?.join(', '),
                  address.city,
                  address.state,
                  address.postalCode,
                  address.country
                ].filter(Boolean);
                
                locationDetail = addressParts.join(', ');
              } else {
                // Se não encontrou endereço no bundle, tenta buscar no contexto
                const contextAddress = getLocationAddress(locationId);
                if (contextAddress) {
                  locationDetail = contextAddress;
                }
              }
            }

            // Se não conseguiu detalhes, usa o nome da localização
            if (!locationDetail && location) {
              locationDetail = location;
            }

            // Busca o paciente nos entries ou no array de pacientes
            let patientName = 'Paciente não informado';
            
            // Primeiro, tenta encontrar o paciente no participant do appointment
            const patientReference = appt.resource?.participant?.find(
              (p: any) => p.actor?.reference?.includes('Patient/')
            )?.actor?.reference;
            
            if (patientReference && nextAppointmentsPatients?.length) {
              // Busca nos nextAppointmentsPatients passados como prop
              const patientEntry = nextAppointmentsPatients.find(
                (patient: any) => patient.resource?.id === patientReference.split('/')[1]
              );
              
              if (patientEntry?.resource?.name?.[0]?.text) {
                patientName = patientEntry.resource.name[0].text;
              }
            }
            
            // Se não encontrou nos nextAppointmentsPatients, tenta nos entries do appointments
            if (patientName === 'Paciente não informado' && patientReference) {
              const patientEntry = appointments?.entry?.find(
                (entry: any) =>
                  entry.resource?.resourceType === 'Patient' &&
                  entry.resource?.id === patientReference.split('/')[1]
              );
              
              if (patientEntry?.resource?.name?.[0]?.text) {
                patientName = patientEntry.resource.name[0].text;
              }
            }

            return {
              id: appt.resource.id,
              start: appt.resource.start,
              end: appt.resource.end,
              location,
              locationDetail,
              virtualServiceUrl,
              practitioner,
              healthcare,
              patientName,
              appointment: appt.resource,
            };
          }) || [];

        setProcessedAppointments(bookedAppointments);
        
        // Verifica se todos os agendamentos já foram adicionados ao Google Calendar
        if (allBookedAppointments.length > 0 && bookedAppointments.length === 0) {
          toast.info('Todos os agendamentos já foram adicionados ao Google Calendar.');
        }
      } catch (error: any) {
        toast.error('Erro ao processar agendamentos');
      } finally {
        setLoading(false);
      }
    }

    processAppointments();
  }, [open, appointments, nextAppointmentsPatients]);

  // Gerencia seleção de appointments
  const handleToggleSelection = (appointmentId: string) => {
    setSelectedAppointments(prev =>
      prev.includes(appointmentId)
        ? prev.filter(id => id !== appointmentId)
        : [...prev, appointmentId]
    );
  };

  // Seleciona/deseleciona todos
  const handleSelectAll = () => {
    if (selectedAppointments.length === processedAppointments.length) {
      setSelectedAppointments([]);
    } else {
      setSelectedAppointments(processedAppointments.map(appt => appt.id));
    }
  };

  // Adiciona appointments selecionados ao Google Calendar
  const handleAddToCalendar = async () => {
    if (selectedAppointments.length === 0) {
      toast.warning('Selecione pelo menos um agendamento');
      return;
    }

    if (!accessTokenGoogle || !user.refreshTokenGoogle) {
      toast.error('Token do Google Calendar não encontrado');
      return;
    }

    setAddingToCalendar(true);
    const newResults: { [id: string]: boolean } = {};

    for (const appointmentId of selectedAppointments) {
      const appointment = processedAppointments.find(appt => appt.id === appointmentId);
      if (!appointment) continue;

      try {
        // Dados do evento para o Google Calendar
        const eventData = {
          summary: `Consulta: ${appointment.healthcare}`,
          description: `Paciente: ${appointment.patientName}`,
          location: appointment.virtualServiceUrl || appointment.locationDetail || appointment.location || '',
          start: {
            dateTime: appointment.start || new Date().toISOString(),
            timeZone: 'America/Sao_Paulo',
          },
          end: {
            dateTime: appointment.end || new Date().toISOString(),
            timeZone: 'America/Sao_Paulo',
          },
        };

        // Cria o evento no Google Calendar
        const eventResponse = await postEvent({
          eventData,
          accessTokenGoogle,
          refreshToken: user.refreshTokenGoogle,
        });

        if (eventResponse?.id) {
          // Atualiza a extension do Google Calendar no appointment
          let newExtensions: any[] = Array.isArray(appointment.appointment?.extension)
            ? appointment.appointment.extension.filter(
                (ext: any) =>
                  ext.url !==
                  'https://fluxmed.com.br/fhir/StructureDefinition/googleCalendarId'
              )
            : [];
          
          newExtensions.unshift({
            url: 'https://fluxmed.com.br/fhir/StructureDefinition/googleCalendarId',
            valueString: eventResponse.id,
          });

          await EditAppointment({
            ...appointment.appointment,
            extension: newExtensions,
          });

          newResults[appointmentId] = true;
        } else {
          newResults[appointmentId] = false;
        }
      } catch (error) {
        newResults[appointmentId] = false;
      }
    }

    setResults(newResults);
    setAddingToCalendar(false);

    // Conta sucessos e falhas
    const successes = Object.values(newResults).filter(Boolean).length;
    const failures = Object.values(newResults).filter(r => !r).length;

    if (successes > 0) {
      toast.success(`${successes} agendamento(s) adicionado(s) ao Google Calendar`);
    }
    if (failures > 0) {
      toast.error(`${failures} agendamento(s) falharam ao adicionar`);
    }

    if (onSuccess) onSuccess();
  };

  const handleClose = () => {
    setSelectedAppointments([]);
    setResults({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '10px', padding: 2 },
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Adicionar ao Google Calendar</Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : processedAppointments.length === 0 ? (
          <Typography variant="body1" textAlign="center" color="text.secondary" py={4}>
            {appointments?.entry?.some((el: any) => 
              el.resource?.resourceType === 'Appointment' && 
              el.resource?.status === 'booked' &&
              new Date(el?.resource?.end) > new Date(Date.now()) &&
              el.resource?.extension?.some((ext: any) => 
                ext.url === 'https://fluxmed.com.br/fhir/StructureDefinition/googleCalendarId'
              )
            ) 
              ? 'Todos os agendamentos desta agenda já foram adicionados ao Google Calendar.'
              : 'Nenhum agendamento confirmado encontrado para esta agenda.'}
          </Typography>
        ) : (
          <>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="body2" color="text.secondary">
                {processedAppointments.length} agendamento(s) confirmado(s) encontrado(s)
              </Typography>
              <SecondaryButton
                width="auto"
                height="36px"
                onClick={handleSelectAll}
              >
                {selectedAppointments.length === processedAppointments.length ? 'Desmarcar todos' : 'Selecionar todos'}
              </SecondaryButton>
            </Stack>

            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {processedAppointments.map((appointment: ProcessedAppointment) => {
                const isSelected = selectedAppointments.includes(appointment.id);
                const result = results[appointment.id];
                
                return (
                  <ListItem
                    key={appointment.id}
                    dense
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      backgroundColor: result === true ? 'success.light' : result === false ? 'error.light' : 'transparent',
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleToggleSelection(appointment.id)}
                        disabled={addingToCalendar}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" fontWeight={500}>
                            {appointment.patientName}
                          </Typography>
                          <Chip
                            label={appointment.healthcare}
                            size="small"
                            variant="outlined"
                          />
                        </Stack>
                      }
                      secondary={
                        <Stack direction="row" spacing={2} mt={0.5}>
                          <Typography variant="caption">
                            📅 {dayjs(appointment.start).format('DD/MM/YYYY')}
                          </Typography>
                          <Typography variant="caption">
                            🕐 {formatDateTimeToHour(appointment.start)}
                          </Typography>
                          <Typography variant="caption">
                            👨‍⚕️ {appointment.practitioner}
                          </Typography>
                        </Stack>
                      }
                    />
                    {result === true && (
                      <Chip label="Adicionado" color="success" size="small" />
                    )}
                    {result === false && (
                      <Chip label="Erro" color="error" size="small" />
                    )}
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <FormButtonGroup
          onGoBackButtonClick={handleClose}
          onNextButtonClick={handleAddToCalendar}
          loading={addingToCalendar}
          disabled={loading || addingToCalendar || selectedAppointments.length === 0}
          goBackButtonText="Fechar"
          nextButtonText={addingToCalendar
            ? 'Adicionando...'
            : `Adicionar ${selectedAppointments.length} selecionado(s)`}
        />
      </DialogActions>
    </Dialog>
  );
} 